import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import API, { SessionAPI } from "../services/api";
import { useSession } from "../context/SessionContext";
import { trackEvent } from "../services/analytics";

const Login = () => {
  const navigate = useNavigate();
  const { setSessionNo } = useSession();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const [showTrialModal, setShowTrialModal] = useState(false);
  const [mobileNo, setMobileNo] = useState("");
  const [trialLoading, setTrialLoading] = useState(false);
  const [otp, setOtp] = useState("");
  const [showOtp, setShowOtp] = useState(false);

  // ============================================================
  // REGISTERED USER LOGIN
  // ============================================================

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email || !password) {
      setError("Please enter email and password");
      return;
    }

    try {
      const res = await API.post(
        "/auth/login",
        {
          email,
          password,
        },
        {
          withCredentials: true,
        }
      );

      const token = res.data?.token;

      if (token) {
        localStorage.setItem("authToken", token);
      }

     // alert(res.data.message);

      const user = res.data.user;

      const userType = user?.userType;
      const userName =
        user?.userName ||
        user?.fullName ||
        user?.email;

      localStorage.setItem(
        "user",
        JSON.stringify(user)
      );

      localStorage.setItem(
        "userType",
        userType ?? ""
      );

      localStorage.setItem(
        "userName",
        userName ?? ""
      );

      localStorage.setItem(
        "userNo",
        user?.userNo?.toString() ?? ""
      );

      navigate("/projects", {
        replace: true,
      });
    } catch (err) {
      setError("Invalid username or password");
    }
  };

  // ============================================================
  // OPEN SAMPLE PROJECT
  // ============================================================

  const openTrialProject = async () => {
    const res = await API.post(
      "/auth/trial-login",
      {
        mobileNo,
      },
      {
        withCredentials: true,
      }
    );

    const token = res.data?.token;

    if (token) {
      localStorage.setItem(
        "authToken",
        token
      );
    }

   // alert(res.data.message);

    const user = res.data.user;

    localStorage.setItem(
      "user",          
      JSON.stringify(user)
    );

    localStorage.setItem(
      "userNo",
      user.userNo.toString()
    );

    localStorage.setItem(
      "userName",
      user.userName
    );

    localStorage.setItem(
      "userType",
      user.userType ?? ""
    );

    const session = await SessionAPI.create(
      res.data.projectNo,
      user.userNo
    );

    setSessionNo(session.sessionNo);

    setShowTrialModal(false);
    setMobileNo("");
    setOtp("");
    setShowOtp(false);

    navigate(
      `/project/${res.data.projectNo}/third`,
      {
        state: {
          projectName: "Sample Project",
        },
      }
    );
  };

  // ============================================================
  // SEND OTP
  // ============================================================

  const handleTrialProject = async () => {
    if (!mobileNo) {
      alert("Please enter Mobile Number");
      return;
    }

    if (mobileNo.length !== 10) {
      alert(
        "Please enter a valid 10 digit Mobile Number"
      );
      return;
    }

    try {
      setTrialLoading(true);

      const res = await API.post(
        "/auth/send-mobile-otp",
        {
          mobileNo,
        },
        {
          withCredentials: true,
        }
      );

      if (!res.data.otpRequired) {
        await openTrialProject();
        return;
      }

      alert(res.data.message);

      setShowOtp(true);
    } catch {
      alert("Unable to send OTP");
    } finally {
      setTrialLoading(false);
    }
  };

  // ============================================================
  // VERIFY OTP
  // ============================================================

  const verifyOtp = async () => {
    if (!otp) {
      alert("Please enter OTP");
      return;
    }

    try {
      setTrialLoading(true);

      await API.post(
        "/auth/verify-mobile-otp",
        {
          mobileNo,
          otp,
        },
        {
          withCredentials: true,
        }
      );

      await openTrialProject();
    } catch (err: any) {
      alert(
        err?.response?.data?.message ??
          "Invalid OTP"
      );
    } finally {
      setTrialLoading(false);
    }
  };

  // ============================================================
  // RESEND OTP
  // ============================================================

  const resendOtp = async () => {
    try {
      setTrialLoading(true);

      await API.post(
        "/auth/send-mobile-otp",
        {
          mobileNo,
        },
        {
          withCredentials: true,
        }
      );

      setOtp("");

      alert(
        "New OTP sent successfully."
      );
    } catch (err: any) {
      alert(
        err?.response?.data?.message ??
          "Unable to resend OTP"
      );
    } finally {
      setTrialLoading(false);
    }
  };

  // ============================================================
  // CLOSE MODAL
  // ============================================================

  const closeTrialModal = () => {
    setShowTrialModal(false);
    setMobileNo("");
    setOtp("");
    setShowOtp(false);
  };

  return (
    <div
      className="
        min-h-screen
        h-auto
        w-full
        overflow-y-auto
        bg-[#F3F4F8]
        flex
        items-center
        justify-center
        px-3
        py-4
        sm:px-5
      "
    >
      {/* ======================================================
          MAIN CARD
      ======================================================= */}

      <div
        className="
          w-full
          max-w-[1150px]
          h-auto
          lg:h-[680px]
          lg:max-h-[90vh]
          bg-white
          rounded-3xl
          shadow-[0_15px_40px_rgba(30,35,60,0.12)]
          overflow-hidden
          flex
        "
      >

        {/* ====================================================
            LEFT SIDE
        ===================================================== */}

        <div
          className="
            hidden
            lg:flex
            lg:w-[56%]
            relative
            overflow-hidden
            bg-white
          "
        >

          {/* TOP PURPLE CIRCLE */}

          <div
            className="
              absolute
              -top-36
              -left-32
              w-[330px]
              h-[330px]
              rounded-full
              bg-[#F0F0FA]
            "
          />

          {/* BOTTOM PURPLE CIRCLE */}

          <div
            className="
              absolute
              -bottom-44
              -right-32
              w-[430px]
              h-[430px]
              rounded-full
              bg-[#F4F3FA]
            "
          />

          <div
            className="
              relative
              z-10
              w-full
              h-full
              px-6
              py-5
              xl:px-7
              xl:py-6
              flex
              flex-col
            "
          >

            {/* ==================================================
                BRAND
            =================================================== */}

            <div className="mb-3">

              <div
                className="
                  text-[34px]
                  font-bold
                  tracking-tight
                  text-gray-600
                  leading-none
                "
              >
                
                <span className="text-[#5B5A98]">
                  m'BIM
                </span>
              </div>

              <p
                className="
                  text-[14px]
                  font-semibold
                  text-gray-500
                  mt-1.5
                "
              >
                Quantity Survey Engine from Baevr
              </p>

            </div>

            {/* ==================================================
                BADGE
            =================================================== */}

            <div
              className="
                inline-flex
                self-start
                items-center
                px-3
                py-1
                rounded-full
                bg-[#F0F0FA]
                text-[#5B5A98]
                text-[10px]
                font-bold
                tracking-wide
                mb-3
              "
            >
              
            </div>

            {/* ==================================================
                HEADING
            =================================================== */}

            <h1
              className="
                text-[34px]
                xl:text-[38px]
                font-bold
                leading-[1.02]
                tracking-tight
                text-gray-800
              "
            >
              One portal
              <br />

              <span className="text-[#5B5A98]">
                All detail
              </span>
            </h1>

            {/* ==================================================
                DESCRIPTION
            =================================================== */}

            <p
              className="
                mt-3
                text-[12px]
                xl:text-[13px]
                leading-relaxed
                font-semibold
                text-gray-500
                max-w-[600px]
              "
            >
              Organize the entire project into a single source of truth <br></br>
              Takeoffs, Line Items, Specifications, Piece Marks
            </p>

            {/* ==================================================
                FEATURE CARDS
            =================================================== */}

            <div
              className="
                grid
                grid-cols-2
                gap-2
                mt-4
                max-w-[600px]
              "
            >

              <FeatureCard
                icon={
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 6h16M4 12h10M4 18h6"
                    />
                  </svg>
                }
                title="Work Breakdown Structure"
                description="Multi-level definition of project work packages"
              />

              <FeatureCard
                icon={
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 6v12M6 12h12"
                    />
                  </svg>
                }
                title="Specifications"
                description="Define specs for individual line items"
              />

              <FeatureCard
                icon={
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 6v12M6 12h12"
                    />
                  </svg>
                }
                title="Parameters"
                description="Explore the modular and parametric QS system"
              />

              <FeatureCard
                icon={
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M7 4h10v16H7zM10 8h4M10 12h4M10 16h4"
                    />
                  </svg>
                }
                title="Bill of Materials"
                description="Generate material and workforce requirements"
              />

              <FeatureCard
                icon={
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 20h9M16.5 3.5a2.121 2.121 0 013 3L8 18l-4 1 1-4L16.5 3.5z"
                    />
                  </svg>
                }
                title="NLBD"
                description="Complete detailed measurement sheets"
              />

              <FeatureCard
                icon={
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h6l5 5v11a2 2 0 01-2 2z"
                    />
                  </svg>
                }
                title="Spreadsheets"
                description="All details in Excel spreadsheet style and form"
              />

            </div>

            {/* ==================================================
                BOTTOM MESSAGE
            =================================================== */}

            <div className="mt-auto pt-4">

              <div className="flex items-center gap-3">

                <div
                  className="
                    w-1
                    h-8
                    rounded-full
                    bg-[#5B5A98]
                  "
                />

                <div>

                  <p
                    className="
                      text-[12px]
                      font-bold
                      text-gray-600
                    "
                  >
                    Detail at BIM Level but Effort at Excel Level
                  </p>

                  <p
                    className="
                      text-[11px]
                      font-semibold
                      text-gray-500
                      mt-1
                    "
                  >
                    Measure everything. Manage everything. 
                  </p>

                </div>

              </div>

            </div>

          </div>
        </div>

        {/* ====================================================
            RIGHT SIDE
        ===================================================== */}

        <div
          className="
            w-full
            lg:w-[44%]
            min-h-0
            flex
            items-center
            justify-center
            px-4
            py-3
            sm:px-5
            lg:px-6
            xl:px-7
          "
        >

          <div className="w-full max-w-[360px]">

            {/* MOBILE BRAND */}

            <div
              className="
                lg:hidden
                text-center
                mb-5
              "
            >

              <div
                className="
                  text-[32px]
                  font-bold
                  text-gray-600
                "
              >
                M
                <span className="text-[#5B5A98]">
                  BIM
                </span>
              </div>

              <p
                className="
                  text-[11px]
                  font-semibold
                  text-gray-500
                  mt-1
                "
              >
                Construction Information Management
              </p>

            </div>

            {/* ==================================================
                WELCOME
            =================================================== */}

            <div className="mb-3">

              <h2
                className="
                  text-[22px]
                  font-bold
                  text-gray-800
                "
              >
                Welcome Back
              </h2>

              <p
                className="
                  text-[12px]
                  font-semibold
                  text-gray-500
                  mt-0.5
                "
              >
                Sign in to access your projects
              </p>

            </div>

            {/* ==================================================
                SAMPLE PROJECT
            =================================================== */}

            <div className="mb-3">

              <div
                className="
                  rounded-xl
                  border
                  border-[#E1E1EC]
                  bg-[#FAFAFD]
                  p-3
                "
              >

                <div className="flex items-start gap-3">

                  <div
                    className="
                      w-8
                      h-8
                      shrink-0
                      rounded-lg
                      bg-[#5B5A98]
                      flex
                      items-center
                      justify-center
                    "
                  >

                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 17v-2m3 2v-6m3 6v-4m3 8H6a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2z"
                      />
                    </svg>

                  </div>

                  <div>

                    <h3
                      className="
                        text-[13px]
                        font-bold
                        text-gray-700
                      "
                    >
                      Explore Sample Project
                    </h3>

                    <p
                      className="
                        text-[10px]
                        font-semibold
                        text-gray-600
                        mt-1
                        leading-relaxed
                      "
                    >
                      Explore the complete project with
                      Work, Workgroups, Properties and
                      BOQ PDFs for every item.
                    </p>

                  </div>

                </div>

                {/* SAMPLE FEATURES */}

                <div
                  className="
                    grid
                    grid-cols-2
                    gap-x-3
                    gap-y-1.5
                    mt-2
                  "
                >
                  <SampleFeature text="Work & Workgroups" />
                  <SampleFeature text="Properties" />
                  <SampleFeature text="Item Parameters" />
                  <SampleFeature text="BOQ PDFs" />
                  <SampleFeature text="Annotations" />
                  <SampleFeature text="Item Details" />
                </div>

                {/* BUTTON */}

               {/*  <button
                  type="button"
                  onClick={() => {
                    setShowTrialModal(true);
                    setMobileNo("");
                    setOtp("");
                    setShowOtp(false);
                  }} */}
                  <button
  type="button"
  onClick={() => {
    trackEvent("sample_project_click", {
      project_name: "Sample Project",
      source: "login_page",
    });

    setShowTrialModal(true);
    setMobileNo("");
    setOtp("");
    setShowOtp(false);
  }}

                  className="
                    w-full
                    mt-3
                    bg-[#5B5A98]
                    hover:bg-[#4D4C83]
                    active:bg-[#434271]
                    text-white
                    py-2
                    rounded-lg
                    text-[12px]
                    font-bold
                    transition
                    shadow-sm
                  "
                >
                  Explore Sample Project
                </button>

              </div>

            </div>

            {/* ==================================================
                DIVIDER
            =================================================== */}

            <div
              className="
                flex
                items-center
                gap-3
                mb-3
              "
            >

              <div className="flex-1 h-px bg-gray-200" />

              <span
                className="
                  text-[10px]
                  font-bold
                  text-gray-500
                "
              >
                REGISTERED USER
              </span>

              <div className="flex-1 h-px bg-gray-200" />

            </div>

            {/* ==================================================
                LOGIN FORM
            =================================================== */}

            <form
              onSubmit={handleSubmit}
              className="space-y-2.5"
            >

              {/* EMAIL */}

              <div>

                <label
                  className="
                    block
                    text-[12px]
                    font-bold
                    text-gray-600
                    mb-1
                  "
                >
                  Email Address
                </label>

                <div className="relative">

                  <div
                    className="
                      absolute
                      left-0
                      top-0
                      bottom-0
                      flex
                      items-center
                      pl-4
                      pointer-events-none
                    "
                  >
                    <svg
                      className="w-4 h-4 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M3 8l9 6 9-6M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </div>

                  <input
                    type="text"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) =>
                      setEmail(e.target.value)
                    }
                    className="
                      w-full
                      border
                      border-gray-200
                      rounded-xl
                      pl-11
                      pr-4
                      py-2
                      bg-gray-50
                      text-[12px]
                      font-semibold
                      text-gray-700
                      placeholder:text-gray-500
                      focus:bg-white
                      focus:outline-none
                      focus:ring-2
                      focus:ring-[#5B5A98]/20
                      focus:border-[#5B5A98]
                      transition
                    "
                  />

                </div>

              </div>

              {/* PASSWORD */}

              <div>

                <div
                  className="
                    flex
                    items-center
                    justify-between
                    mb-1.5
                  "
                >

                  <label
                    className="
                      block
                      text-[12px]
                      font-bold
                      text-gray-600
                    "
                  >
                    Password
                  </label>

                  <button
                    type="button"
                    onClick={() =>
                      navigate("/forgot-password")
                    }
                    className="
                      text-[11px]
                      font-bold
                      text-[#5B5A98]
                      hover:text-[#4D4C83]
                      hover:underline
                    "
                  >
                    Forgot Password?
                  </button>

                </div>

                <div className="relative">

                  <div
                    className="
                      absolute
                      left-0
                      top-0
                      bottom-0
                      flex
                      items-center
                      pl-4
                      pointer-events-none
                    "
                  >
                    <svg
                      className="w-4 h-4 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-7a2 2 0 00-2-2H6a2 2 0 00-2 2v7a2 2 0 002 2zm10-11V7a4 4 0 10-8 0v3h8z"
                      />
                    </svg>
                  </div>

                  <input
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) =>
                      setPassword(e.target.value)
                    }
                    className="
                      w-full
                      border
                      border-gray-200
                      rounded-xl
                      pl-11
                      pr-4
                      py-2
                      bg-gray-50
                      text-[12px]
                      font-semibold
                      text-gray-700
                      placeholder:text-gray-500
                      focus:bg-white
                      focus:outline-none
                      focus:ring-2
                      focus:ring-[#5B5A98]/20
                      focus:border-[#5B5A98]
                      transition
                    "
                  />

                </div>

              </div>

              {/* ERROR */}

              {error && (
                <div
                  className="
                    bg-red-50
                    border
                    border-red-200
                    rounded-xl
                    px-4
                    py-2.5
                    text-[12px]
                    font-semibold
                    text-red-600
                  "
                >
                  {error}
                </div>
              )}

              {/* SIGN IN */}

              <button
                type="submit"
                className="
                  w-full
                  bg-[#5B5A98]
                  hover:bg-[#4D4C83]
                  active:bg-[#434271]
                  text-white
                  py-2
                  rounded-xl
                  text-[12px]
                  font-bold
                  transition
                  shadow-sm
                  hover:shadow-md
                "
              >
                Sign In
              </button>

              {/* CREATE ACCOUNT */}

              <div className="text-center pt-1">

                <p
                  className="
                    text-[13px]
                    font-semibold
                    text-gray-600
                  "
                >
                  New user?{" "}

                  <span
                    onClick={() =>
                      navigate("/register")
                    }
                    className="
                      text-[#5B5A98]
                      font-bold
                      cursor-pointer
                      hover:underline
                    "
                  >
                    Create account
                  </span>
                </p>

              </div>

              {/* COMPANY */}

              <div
                className="
                  text-center
                  pt-2.5
                  border-t
                  border-gray-100
                "
              >

                <p
                  className="
                    text-[11px]
                    font-semibold
                    text-gray-500
                    mb-1
                  "
                >
                  Are you registering a company?
                </p>

                <span
                  onClick={() =>
                    navigate("/register-company")
                  }
                  className="
                    text-[13px]
                    text-[#5B5A98]
                    font-bold
                    cursor-pointer
                    hover:underline
                  "
                >
                  Register a Company
                </span>

              </div>

            </form>

            {/* FOOTER */}

            <div
              className="
                text-center
                mt-2
              "
            >

              <p
                className="
                  text-[10px]
                  font-semibold
                  text-gray-500
                "
              >
                © {new Date().getFullYear()} Agile Civil Engg
              </p>

              <p
                className="
                  text-[10px]
                  font-semibold
                  text-gray-500
                  mt-1
                "
              >
                MBIM · Construction Information Management
              </p>

            </div>

          </div>
        </div>
      </div>

      {/* ========================================================
          SAMPLE PROJECT / OTP MODAL
      ========================================================= */}

      {showTrialModal && (
        <div
          className="
            fixed
            inset-0
            z-50
            bg-gray-900/50
            backdrop-blur-sm
            flex
            items-center
            justify-center
            px-4
          "
        >

          <div
            className="
              w-full
              max-w-md
              bg-white
              rounded-2xl
              shadow-2xl
              p-6
            "
          >

            {/* MODAL HEADER */}

            <div
              className="
                flex
                items-start
                justify-between
                mb-5
              "
            >

              <div className="flex items-center gap-3">

                <div
                  className="
                    w-10
                    h-10
                    rounded-xl
                    bg-[#F0F0FA]
                    flex
                    items-center
                    justify-center
                  "
                >

                  <svg
                    className="w-5 h-5 text-[#5B5A98]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 17v-2m3 2v-6m3 6v-4m3 8H6a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2z"
                    />
                  </svg>

                </div>

                <div>

                  <h2
                    className="
                      text-[18px]
                      font-bold
                      text-gray-700
                    "
                  >
                    Sample Project
                  </h2>

                  <p
                    className="
                      text-[11px]
                      font-semibold
                      text-gray-500
                    "
                  >
                    Explore the complete project
                  </p>

                </div>

              </div>

              <button
                type="button"
                onClick={closeTrialModal}
                className="
                  text-gray-400
                  hover:text-gray-600
                  text-2xl
                  leading-none
                "
              >
                ×
              </button>

            </div>

            {/* DESCRIPTION */}

            {!showOtp && (
              <div
                className="
                  bg-[#FAFAFD]
                  border
                  border-[#E1E1EC]
                  rounded-xl
                  p-3
                  mb-5
                "
              >

                <p
                  className="
                    text-[12px]
                    font-semibold
                    text-gray-600
                    leading-relaxed
                  "
                >
                  Enter your mobile number to access the
                  sample project and explore Work,
                  Workgroups, Properties, Parameters,
                  Annotations and BOQ PDFs for every item.
                </p>
                <p
                 className="
                  mt-2
                  text-[11px]
                  font-semibold
                  text-[#5B5A98]
                  leading-relaxed
                "
              >
  Note: OTP verification is required for first-time registration.
</p>

              </div>
            )}

            {/* MOBILE */}

            <label
              className="
                block
                text-[13px]
                font-bold
                text-gray-600
                mb-2
              "
            >
              Mobile Number
            </label>

            <input
              type="text"
              value={mobileNo}
              maxLength={10}
              placeholder="Enter 10 digit mobile number"
              onChange={(e) => {
                setMobileNo(
                  e.target.value.replace(/\D/g, "")
                );

                setOtp("");
                setShowOtp(false);
              }}
              className="
                w-full
                border
                border-gray-200
                rounded-xl
                px-4
                py-3
                bg-gray-50
                text-[13px]
                font-semibold
                text-gray-700
                placeholder:text-gray-500
                focus:bg-white
                focus:outline-none
                focus:ring-2
                focus:ring-[#5B5A98]/20
                focus:border-[#5B5A98]
              "
            />

            {/* OTP */}

            {showOtp && (
              <div className="mt-4">

                <label
                  className="
                    block
                    text-[13px]
                    font-bold
                    text-gray-600
                    mb-2
                  "
                >
                  OTP
                </label>

                <input
                  type="text"
                  value={otp}
                  maxLength={6}
                  placeholder="Enter OTP"
                  onChange={(e) =>
                    setOtp(
                      e.target.value.replace(
                        /\D/g,
                        ""
                      )
                    )
                  }
                  className="
                    w-full
                    border
                    border-gray-200
                    rounded-xl
                    px-4
                    py-3
                    bg-gray-50
                    text-[13px]
                    font-semibold
                    text-gray-700
                    placeholder:text-gray-500
                    focus:bg-white
                    focus:outline-none
                    focus:ring-2
                    focus:ring-[#5B5A98]/20
                    focus:border-[#5B5A98]
                  "
                />

                <div className="flex justify-end mt-2">

                  <button
                    type="button"
                    onClick={resendOtp}
                    disabled={trialLoading}
                    className="
                      text-[12px]
                      font-bold
                      text-[#5B5A98]
                      hover:underline
                    "
                  >
                    Resend OTP
                  </button>

                </div>

              </div>
            )}

            {/* MODAL BUTTONS */}

            <div
              className="
                flex
                justify-end
                gap-3
                mt-6
              "
            >

              <button
                type="button"
                onClick={closeTrialModal}
                className="
                  px-5
                  py-2.5
                  rounded-xl
                  bg-gray-100
                  hover:bg-gray-200
                  text-gray-600
                  text-[13px]
                  font-bold
                "
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={
                  showOtp
                    ? verifyOtp
                    : handleTrialProject
                }
                disabled={trialLoading}
                className="
                  px-5
                  py-2.5
                  rounded-xl
                  bg-[#5B5A98]
                  hover:bg-[#4D4C83]
                  active:bg-[#434271]
                  text-white
                  text-[13px]
                  font-bold
                  disabled:opacity-60
                "
              >
                {trialLoading
                  ? "Please Wait..."
                  : showOtp
                  ? "Verify OTP"
                  : "Continue"}
              </button>

            </div>

          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// FEATURE CARD
// ============================================================

type FeatureCardProps = {
  icon: React.ReactNode;
  title: string;
  description: string;
};

const FeatureCard = ({
  icon,
  title,
  description,
}: FeatureCardProps) => {
  return (
    <div
      className="
        bg-white
        border
        border-[#E3E3EB]
        rounded-xl
        p-2
        shadow-sm
        hover:border-[#CFCFE1]
        transition
      "
    >

      <div
        className="
          w-6
          h-6
          rounded-lg
          bg-[#F0F0FA]
          text-[#5B5A98]
          flex
          items-center
          justify-center
          mb-1
        "
      >
        {icon}
      </div>

      {/* TITLE - DARKER + THICKER */}

      <h3
        className="
          text-[11px]
          font-bold
          text-gray-700
        "
      >
        {title}
      </h3>

      {/* DESCRIPTION - DARKER + THICKER */}

      <p
        className="
          text-[9px]
          font-semibold
          text-gray-500
          mt-0.5
        "
      >
        {description}
      </p>

    </div>
  );
};

// ============================================================
// SAMPLE PROJECT FEATURE
// ============================================================

const SampleFeature = ({
  text,
}: {
  text: string;
}) => {
  return (
    <div
      className="
        flex
        items-center
        gap-2
        text-[11px]
        font-semibold
        text-gray-600
      "
    >

      <span
        className="
          w-1.5
          h-1.5
          rounded-full
          bg-[#5B5A98]
          shrink-0
        "
      />

      {text}

    </div>
  );
};

export default Login;